import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const AdminNoticeEventManagement = ({ onNavigate, userInfo = {} }) => {
  // Mobile state (used for responsive content layout)
  const [isMobile, setIsMobile] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState('notices'); // 'notices' or 'events'

  // State for data
  const [notices, setNotices] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination and search state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [limit] = useState(10);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '', // for notices
    description: '', // for events
    event_date: '', // for events only
    status: 'draft'
  });

  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Reset pagination when tab changes
  useEffect(() => {
    setCurrentPage(1);
    setSearchTerm('');
    setStatusFilter('');
    if (activeTab === 'notices') {
      fetchNotices(1, '', '');
    } else {
      fetchEvents(1, '', '');
    }
  }, [activeTab]);

  // Fetch notices from API
  const fetchNotices = async (page = 1, search = '', status = '') => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found. Please login again.');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && search.trim() && { search: search.trim() }),
        ...(status && status.trim() && { status: status.trim() })
      });

      const apiUrl = `${API_LINK}/api/admin/notices?${params}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        const noticesData = result.data?.notices || [];
        const paginationData = result.data?.pagination || {};

        setNotices(noticesData);
        setCurrentPage(paginationData.currentPage || page);
        setTotalPages(paginationData.totalPages || 1);
        setTotalCount(paginationData.totalCount || noticesData.length);
      } else {
        setError(result.message || 'Failed to fetch notices from server');
      }
    } catch (error) {
      setError(`Failed to load notices: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch events from API
  const fetchEvents = async (page = 1, search = '', status = '') => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found. Please login again.');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && search.trim() && { search: search.trim() }),
        ...(status && status.trim() && { status: status.trim() })
      });

      const apiUrl = `${API_LINK}/api/admin/events?${params}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        const eventsData = result.data?.events || [];
        const paginationData = result.data?.pagination || {};

        setEvents(eventsData);
        setCurrentPage(paginationData.currentPage || page);
        setTotalPages(paginationData.totalPages || 1);
        setTotalCount(paginationData.totalCount || eventsData.length);
      } else {
        setError(result.message || 'Failed to fetch events from server');
      }
    } catch (error) {
      setError(`Failed to load events: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === 'notices') {
        fetchNotices(1, '', '');
      } else {
        fetchEvents(1, '', '');
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Handle search and filters
  const handleSearch = () => {
    setCurrentPage(1);
    if (activeTab === 'notices') {
      fetchNotices(1, searchTerm, statusFilter);
    } else {
      fetchEvents(1, searchTerm, statusFilter);
    }
  };

  // Handle pagination
  const handlePageChange = (page) => {
    if (page && page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      if (activeTab === 'notices') {
        fetchNotices(page, searchTerm, statusFilter);
      } else {
        fetchEvents(page, searchTerm, statusFilter);
      }
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    } else if (formData.title.length > 255) {
      errors.title = 'Title must be 255 characters or less';
    }

    if (activeTab === 'notices') {
      if (!formData.content.trim()) {
        errors.content = 'Content is required';
      }
    } else {
      if (!formData.description.trim()) {
        errors.description = 'Description is required';
      }
      if (!formData.event_date) {
        errors.event_date = 'Event date is required';
      }
    }

    if (!formData.status.trim()) {
      errors.status = 'Status is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission for add/edit
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const endpoint = activeTab === 'notices' ? 'notices' : 'events';
      const url = selectedItem
        ? `${API_LINK}/api/admin/${endpoint}/${selectedItem.id}`
        : `${API_LINK}/api/admin/${endpoint}`;

      const method = selectedItem ? 'PUT' : 'POST';

      // Prepare data based on type
      const submitData = {
        title: formData.title,
        status: formData.status
      };

      if (activeTab === 'notices') {
        submitData.content = formData.content;
      } else {
        submitData.description = formData.description;
        submitData.event_date = formData.event_date;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      const result = await response.json();

      if (result.success) {
        setShowAddModal(false);
        setShowEditModal(false);
        resetForm();

        if (activeTab === 'notices') {
          fetchNotices(currentPage || 1, searchTerm, statusFilter);
        } else {
          fetchEvents(currentPage || 1, searchTerm, statusFilter);
        }

        alert(selectedItem
          ? `${activeTab === 'notices' ? 'Notice' : 'Event'} updated successfully!`
          : `${activeTab === 'notices' ? 'Notice' : 'Event'} added successfully!`);
      } else {
        alert(result.message || 'Operation failed');
      }
    } catch (error) {
      alert(`Failed to save ${activeTab === 'notices' ? 'notice' : 'event'}. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedItem) return;

    try {
      setSubmitting(true);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const endpoint = activeTab === 'notices' ? 'notices' : 'events';
      const response = await fetch(`${API_LINK}/api/admin/${endpoint}/${selectedItem.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.success) {
        setShowDeleteModal(false);
        setSelectedItem(null);

        if (activeTab === 'notices') {
          fetchNotices(currentPage || 1, searchTerm, statusFilter);
        } else {
          fetchEvents(currentPage || 1, searchTerm, statusFilter);
        }

        alert(`${activeTab === 'notices' ? 'Notice' : 'Event'} deleted successfully!`);
      } else {
        alert(result.message || 'Delete failed');
      }
    } catch (error) {
      alert(`Failed to delete ${activeTab === 'notices' ? 'notice' : 'event'}. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      description: '',
      event_date: '',
      status: 'draft'
    });
    setFormErrors({});
    setSelectedItem(null);
  };

  // Handle edit click
  const handleEditClick = (item) => {
    setSelectedItem(item);
    setFormData({
      title: item.title || '',
      content: item.content || '',
      description: item.description || '',
      event_date: item.event_date || '',
      status: item.status || 'draft'
    });
    setShowEditModal(true);
  };

  // Handle delete click
  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };



  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format event date
  const formatEventDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Truncate content for display
  const truncateContent = (content, maxLength = 100) => {
    if (!content) return '';
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  // Get current data based on active tab
  const getCurrentData = () => {
    return activeTab === 'notices' ? notices : events;
  };

  return (
    <Layout role="admin" onNavigate={onNavigate} activePage="admin-notices" userInfo={userInfo}>
      {/* Welcome Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
        color: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
          Notice & Event Management Portal 📢🎉
        </h2>
        <p style={{ margin: 0, opacity: 0.9 }}>
          Manage all notices and events, create new announcements, and maintain comprehensive information.
        </p>
      </div>

      {/* Tab Navigation */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px 12px 0 0',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: 0
      }}>
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e2e8f0'
        }}>
          <button
            onClick={() => setActiveTab('notices')}
            style={{
              flex: 1,
              padding: '1rem 2rem',
              background: activeTab === 'notices' ? '#238548' : 'transparent',
              color: activeTab === 'notices' ? '#ffffff' : '#666',
              border: 'none',
              borderRadius: '12px 0 0 0',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            📢 Notices
          </button>
          <button
            onClick={() => setActiveTab('events')}
            style={{
              flex: 1,
              padding: '1rem 2rem',
              background: activeTab === 'events' ? '#238548' : 'transparent',
              color: activeTab === 'events' ? '#ffffff' : '#666',
              border: 'none',
              borderRadius: '0 12px 0 0',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            🎉 Events
          </button>
        </div>
      </div>

      {/* Search and Controls Section */}
      <div style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '0 0 12px 12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          {/* Search Bar */}
          <div style={{ position: 'relative', maxWidth: '400px' }}>
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              style={{
                width: '100%',
                padding: '1rem 3rem 1rem 1rem',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'all 0.3s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#238548';
                e.target.style.boxShadow = '0 0 0 3px rgba(35, 133, 72, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.boxShadow = 'none';
              }}
            />
            <button
              onClick={handleSearch}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                fontSize: '1.2rem',
                cursor: 'pointer',
                color: '#666',
                padding: '0.5rem'
              }}
            >
              🔍
            </button>
          </div>

          {/* Filters and Add Button */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: '1rem',
            flexWrap: 'wrap'
          }}>
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '0.75rem',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                minWidth: '150px'
              }}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
            </select>

            {/* Search Button */}
            <button
              onClick={handleSearch}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap'
              }}
            >
              Apply Filters
            </button>

            {/* Add Button */}
            <button
              onClick={() => {
                resetForm();
                setShowAddModal(true);
              }}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                whiteSpace: 'nowrap',
                marginLeft: 'auto'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <span style={{ fontSize: '1.2rem' }}>+</span>
              Add New {activeTab === 'notices' ? 'Notice' : 'Event'}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '1rem',
          borderTop: '1px solid #ecf0f1',
          fontSize: '0.9rem',
          color: '#666'
        }}>
          <span>
            {loading ? `Loading ${activeTab}...` :
              error ? `Error loading ${activeTab}` :
                `Showing ${getCurrentData().length} of ${totalCount} ${activeTab}`}
            {(searchTerm || statusFilter) && !loading && !error && ` (filtered)`}
          </span>
          {loading && (
            <span style={{ color: '#238548' }}>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #e3f2fd',
                borderTop: '2px solid #238548',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                display: 'inline-block'
              }}></div>
            </span>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fecaca',
          color: '#dc2626',
          padding: '1rem',
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>⚠️ Error Loading {activeTab}</p>
          <p style={{ margin: '0 0 1rem 0' }}>{error}</p>
          <button
            onClick={() => {
              if (activeTab === 'notices') {
                fetchNotices(currentPage, searchTerm, statusFilter);
              } else {
                fetchEvents(currentPage, searchTerm, statusFilter);
              }
            }}
            style={{
              background: '#dc2626',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Data Table */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {loading && getCurrentData().length === 0 ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '4rem',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #e3f2fd',
              borderTop: '4px solid #238548',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading {activeTab}...</p>
            <style>
              {`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}
            </style>
          </div>
        ) : getCurrentData().length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.5 }}>
              {activeTab === 'notices' ? '📢' : '🎉'}
            </div>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: '#333',
              margin: '0 0 1rem 0'
            }}>
              {searchTerm || statusFilter ? `No ${activeTab} found` : `No ${activeTab} created yet`}
            </h3>
            <p style={{ color: '#666', margin: '0 0 2rem 0', fontSize: '1rem' }}>
              {searchTerm || statusFilter
                ? 'Try adjusting your search criteria or filters'
                : `Get started by adding your first ${activeTab === 'notices' ? 'notice' : 'event'} to the system`
              }
            </p>
            {!(searchTerm || statusFilter) && (
              <button
                onClick={() => {
                  resetForm();
                  setShowAddModal(true);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Add First {activeTab === 'notices' ? 'Notice' : 'Event'}
              </button>
            )}
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: isMobile ? '0.85rem' : '0.95rem'
              }}>
                <thead>
                  <tr style={{
                    background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                    color: '#ffffff'
                  }}>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Title</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>{activeTab === 'notices' ? 'Content' : 'Description'}</th>
                    {activeTab === 'events' && (
                      <th style={{
                        padding: '1rem',
                        textAlign: 'left',
                        fontWeight: 600,
                        borderBottom: 'none',
                        whiteSpace: 'nowrap'
                      }}>Event Date</th>
                    )}
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Status</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Created</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Updated</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'center',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getCurrentData().map((item, index) => (
                    <tr key={item.id} style={{
                      borderBottom: '1px solid #f1f5f9',
                      transition: 'background-color 0.3s ease'
                    }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <td style={{ padding: '1rem', fontWeight: 500, color: '#333', maxWidth: '200px' }}>
                        {item.title}
                      </td>
                      <td style={{ padding: '1rem', color: '#666', maxWidth: '300px' }}>
                        {truncateContent(activeTab === 'notices' ? item.content : item.description, 80)}
                      </td>
                      {activeTab === 'events' && (
                        <td style={{ padding: '1rem', color: '#666', fontSize: '0.9rem' }}>
                          {formatEventDate(item.event_date)}
                        </td>
                      )}
                      <td style={{ padding: '1rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          backgroundColor: item.status === 'active' ? '#e8f5e9' : '#fff3cd',
                          color: item.status === 'active' ? '#2e7d32' : '#856404',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          textTransform: 'capitalize'
                        }}>
                          {item.status === 'active' ? '🟢 Active' : '🟡 Draft'}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', color: '#666', fontSize: '0.9rem' }}>
                        {formatDate(item.created_at)}
                      </td>
                      <td style={{ padding: '1rem', color: '#666', fontSize: '0.9rem' }}>
                        {formatDate(item.updated_at)}
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button
                            onClick={() => handleEditClick(item)}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: '#e3f2fd',
                              color: '#1976d2',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              fontWeight: 500
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#1976d2';
                              e.target.style.color = '#ffffff';
                              e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#e3f2fd';
                              e.target.style.color = '#1976d2';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleDeleteClick(item)}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: '#ffebee',
                              color: '#d32f2f',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              fontWeight: 500
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#d32f2f';
                              e.target.style.color = '#ffffff';
                              e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#ffebee';
                              e.target.style.color = '#d32f2f';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                padding: '1.5rem',
                borderTop: '1px solid #f1f5f9',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{ color: '#666', fontSize: '0.9rem' }}>
                  Page {currentPage} of {totalPages} • {totalCount} total {activeTab}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #e2e8f0',
                      backgroundColor: currentPage === 1 ? '#f8fafc' : '#ffffff',
                      color: currentPage === 1 ? '#9ca3af' : '#374151',
                      borderRadius: '6px',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.borderColor = '#238548';
                        e.target.style.backgroundColor = '#f0fdf4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.backgroundColor = '#ffffff';
                      }
                    }}
                  >
                    ← Previous
                  </button>

                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    let page;
                    if (totalPages <= 5) {
                      page = index + 1;
                    } else if (currentPage <= 3) {
                      page = index + 1;
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + index;
                    } else {
                      page = currentPage - 2 + index;
                    }

                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          border: '2px solid',
                          borderColor: page === currentPage ? '#238548' : '#e2e8f0',
                          backgroundColor: page === currentPage ? '#238548' : '#ffffff',
                          color: page === currentPage ? '#ffffff' : '#374151',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          fontSize: '0.9rem',
                          fontWeight: page === currentPage ? '600' : '400'
                        }}
                        onMouseEnter={(e) => {
                          if (page !== currentPage) {
                            e.target.style.borderColor = '#238548';
                            e.target.style.backgroundColor = '#f0fdf4';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (page !== currentPage) {
                            e.target.style.borderColor = '#e2e8f0';
                            e.target.style.backgroundColor = '#ffffff';
                          }
                        }}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #e2e8f0',
                      backgroundColor: currentPage === totalPages ? '#f8fafc' : '#ffffff',
                      color: currentPage === totalPages ? '#9ca3af' : '#374151',
                      borderRadius: '6px',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.borderColor = '#238548';
                        e.target.style.backgroundColor = '#f0fdf4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.backgroundColor = '#ffffff';
                      }
                    }}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            width: '100%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
              color: '#ffffff',
              padding: '1.5rem 2rem',
              borderRadius: '12px 12px 0 0'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                {selectedItem ? '✏️' : '➕'} {selectedItem ? 'Edit' : 'Add New'} {activeTab === 'notices' ? 'Notice' : 'Event'}
              </h3>
              <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
                {selectedItem ? `Update ${activeTab === 'notices' ? 'notice' : 'event'} information` : `Enter new ${activeTab === 'notices' ? 'notice' : 'event'} details`}
              </p>
            </div>

            {/* Modal Content */}
            <div style={{
              padding: '2rem',
              overflowY: 'auto',
              flex: 1
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
                {/* Title */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder={`Enter ${activeTab === 'notices' ? 'notice' : 'event'} title`}
                    maxLength={255}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.title ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.title ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.title) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.title) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                    {formErrors.title && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: 0 }}>
                        {formErrors.title}
                      </p>
                    )}
                    <p style={{ color: '#666', fontSize: '0.8rem', margin: 0, marginLeft: 'auto' }}>
                      {formData.title.length}/255 characters
                    </p>
                  </div>
                </div>

                {/* Event Date (Events Only) */}
                {activeTab === 'events' && (
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Event Date *
                    </label>
                    <input
                      type="date"
                      value={formData.event_date}
                      onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.event_date ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.event_date ? '#fef2f2' : '#ffffff'
                      }}
                      onFocus={(e) => {
                        if (!formErrors.event_date) e.target.style.borderColor = '#238548';
                      }}
                      onBlur={(e) => {
                        if (!formErrors.event_date) e.target.style.borderColor = '#e5e7eb';
                      }}
                    />
                    {formErrors.event_date && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.event_date}
                      </p>
                    )}
                  </div>
                )}

                {/* Status */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Status *
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.status ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.status ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.status) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.status) e.target.style.borderColor = '#e5e7eb';
                    }}
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                  </select>
                  {formErrors.status && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.status}
                    </p>
                  )}
                  <p style={{ color: '#666', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                    {formData.status === 'active' ? `${activeTab === 'notices' ? 'Notice' : 'Event'} will be visible to all users` : `${activeTab === 'notices' ? 'Notice' : 'Event'} will be saved as draft`}
                  </p>
                </div>

                {/* Content/Description */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    {activeTab === 'notices' ? 'Content' : 'Description'} *
                  </label>
                  <textarea
                    value={activeTab === 'notices' ? formData.content : formData.description}
                    onChange={(e) => setFormData({
                      ...formData,
                      [activeTab === 'notices' ? 'content' : 'description']: e.target.value
                    })}
                    placeholder={`Enter ${activeTab === 'notices' ? 'notice content' : 'event description'}...`}
                    rows={8}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${(activeTab === 'notices' ? formErrors.content : formErrors.description) ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: (activeTab === 'notices' ? formErrors.content : formErrors.description) ? '#fef2f2' : '#ffffff',
                      resize: 'vertical',
                      minHeight: '150px'
                    }}
                    onFocus={(e) => {
                      if (!(activeTab === 'notices' ? formErrors.content : formErrors.description)) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!(activeTab === 'notices' ? formErrors.content : formErrors.description)) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {(activeTab === 'notices' ? formErrors.content : formErrors.description) && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {activeTab === 'notices' ? formErrors.content : formErrors.description}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1.5rem 2rem',
              borderTop: '1px solid #f1f5f9',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '1rem',
              backgroundColor: '#f8fafc'
            }}>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  resetForm();
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = '#ffffff';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  padding: '0.75rem 2rem',
                  border: 'none',
                  background: submitting
                    ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                    : 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                  color: '#ffffff',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                {submitting ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Saving...
                  </>
                ) : (
                  <>
                    {selectedItem ? '💾 Update' : '➕ Add'} {activeTab === 'notices' ? 'Notice' : 'Event'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            maxWidth: '400px',
            width: '100%'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '2rem 2rem 1rem 2rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: '#fee2e2',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '2rem'
              }}>
                🗑️
              </div>
              <h3 style={{
                margin: '0 0 1rem 0',
                fontSize: '1.5rem',
                fontWeight: 600,
                color: '#dc2626'
              }}>
                Delete {activeTab === 'notices' ? 'Notice' : 'Event'}
              </h3>
              <p style={{
                margin: '0 0 1rem 0',
                color: '#6b7280',
                lineHeight: 1.6
              }}>
                Are you sure you want to delete the {activeTab === 'notices' ? 'notice' : 'event'} <strong style={{ color: '#374151' }}>"{selectedItem.title}"</strong>?
              </p>
              <p style={{
                margin: 0,
                color: '#dc2626',
                fontSize: '0.9rem',
                fontWeight: 500
              }}>
                This action cannot be undone and will permanently remove the {activeTab === 'notices' ? 'notice' : 'event'}.
              </p>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1rem 2rem 2rem 2rem',
              display: 'flex',
              justifyContent: 'center',
              gap: '1rem'
            }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedItem(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = '#ffffff';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={submitting}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  background: submitting
                    ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                    : 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
                  color: '#ffffff',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(220, 38, 38, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                {submitting ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Deleting...
                  </>
                ) : (
                  `🗑️ Delete ${activeTab === 'notices' ? 'Notice' : 'Event'}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminNoticeEventManagement;